import type { AxiosInstance, InternalAxiosRequestConfig } from "axios";

import { config } from "@shared/config";

import {
  getMaintenanceBypass,
  isMaintenanceResponse,
  useMaintenanceStore,
} from "./maintenance-store";

const apiBase = () => config.VITE_MC_API?.replace(/\/+$/, "") ?? "";

// The global axios also calls third parties (uploads, meta.json); a custom
// header there would fail their CORS preflight.
const targetsApi = ({ baseURL, url = "" }: InternalAxiosRequestConfig) => {
  const base = apiBase();

  if (!base) {
    return false;
  }

  const target = /^https?:\/\//.test(url) ? url : `${baseURL ?? ""}${url}`;

  return target === base || target.startsWith(`${base}/`);
};

/**
 * Sends the bypass token when set, and switches the app to its maintenance
 * page as soon as the API answers 503 MAINTENANCE.
 *
 * Needed on every axios.create() instance: they do not inherit the
 * interceptors registered on the global axios.
 */
export const attachMaintenanceInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use((request) => {
    const bypass = getMaintenanceBypass();

    if (bypass && targetsApi(request)) {
      request.headers.set("X-Maintenance-Bypass", bypass);
    }

    return request;
  });

  instance.interceptors.response.use(undefined, (error) => {
    const { status, data } = error?.response ?? {};

    if (isMaintenanceResponse(status, data)) {
      const { active, message, startsAt, until } = data;

      useMaintenanceStore
        .getState()
        .setStatus({ active, message, startsAt, until });
    }

    return Promise.reject(error);
  });

  return instance;
};
